// Loan Compliance Module v1.0 PR 7 + PR 9 — TanStack Query hooks for the
// Track 3 Annual Legal Compliance Review (loan and pool scopes).

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  legalReviewMemosApi,
  loanLegalReviewsApi,
  poolLegalReviewMemosApi,
  poolLegalReviewsApi,
} from "@/api/compliance/legalReviewClient";
import type {
  CreateLegalReviewMemoRequest,
  CreateLoanLegalReviewRequest,
  CreatePoolLegalReviewMemoRequest,
  CreatePoolLegalReviewRequest,
  FinalizeLoanLegalReviewRequest,
  FinalizePoolLegalReviewRequest,
  LegalReviewMemoDto,
  LoanLegalReviewDto,
  LoanLegalReviewSummaryDto,
  PoolLegalReviewDto,
  PoolLegalReviewMemoDto,
  PoolLegalReviewSummaryDto,
  PoolSupervisorAcceptanceRequest,
  RecordModuleConclusionRequest,
  RecordPoolModuleConclusionRequest,
  ReissueLoanLegalReviewRequest,
  ReissuePoolLegalReviewRequest,
  SignLegalReviewMemoRequest,
  SignPoolLegalReviewMemoRequest,
  SupervisorAcceptanceRequest,
  UpdatePoolScopingMemoRequest,
  UpdateScopingMemoRequest,
} from "@/api/compliance/legalReviewTypes";

export const legalReviewKeys = {
  reviewsForLoan: (loanId: string) =>
    ["compliance", "legal-review", "loan", loanId, "reviews"] as const,
  review: (loanId: string, reviewId: string) =>
    [
      "compliance",
      "legal-review",
      "loan",
      loanId,
      "review",
      reviewId,
    ] as const,
  memos: (loanId: string) =>
    ["compliance", "legal-review", "loan", loanId, "memos"] as const,
  reviewsForPool: (poolId: string) =>
    ["compliance", "legal-review", "pool", poolId, "reviews"] as const,
  poolReview: (poolId: string, reviewId: string) =>
    [
      "compliance",
      "legal-review",
      "pool",
      poolId,
      "review",
      reviewId,
    ] as const,
  poolMemos: (poolId: string) =>
    ["compliance", "legal-review", "pool", poolId, "memos"] as const,
};

function invalidateReview(qc: QueryClient, loanId: string, reviewId?: string) {
  qc.invalidateQueries({ queryKey: legalReviewKeys.reviewsForLoan(loanId) });
  if (reviewId) {
    qc.invalidateQueries({ queryKey: legalReviewKeys.review(loanId, reviewId) });
  }
  qc.invalidateQueries({
    queryKey: ["compliance", "exceptions"],
  });
}

function invalidatePoolReview(
  qc: QueryClient,
  poolId: string,
  reviewId?: string,
) {
  qc.invalidateQueries({ queryKey: legalReviewKeys.reviewsForPool(poolId) });
  if (reviewId) {
    qc.invalidateQueries({
      queryKey: legalReviewKeys.poolReview(poolId, reviewId),
    });
  }
  qc.invalidateQueries({
    queryKey: ["compliance", "exceptions"],
  });
}

// ============================== Engagement reads (loan) ==================

export function useLoanLegalReviews(loanId: string | undefined) {
  return useQuery<Array<LoanLegalReviewDto | LoanLegalReviewSummaryDto>>({
    queryKey: legalReviewKeys.reviewsForLoan(loanId ?? ""),
    queryFn: () => loanLegalReviewsApi.list(loanId!),
    enabled: !!loanId,
  });
}

export function useLoanLegalReview(
  loanId: string | undefined,
  reviewId: string | undefined,
) {
  return useQuery<LoanLegalReviewDto | LoanLegalReviewSummaryDto>({
    queryKey: legalReviewKeys.review(loanId ?? "", reviewId ?? ""),
    queryFn: () => loanLegalReviewsApi.get(loanId!, reviewId!),
    enabled: !!loanId && !!reviewId,
  });
}

// ============================== Engagement writes (loan) =================

export function useCreateLoanLegalReview(loanId: string) {
  const qc = useQueryClient();
  return useMutation<
    LoanLegalReviewDto,
    unknown,
    CreateLoanLegalReviewRequest
  >({
    mutationFn: (body) => loanLegalReviewsApi.create(loanId, body),
    onSuccess: () => invalidateReview(qc, loanId),
  });
}

export function useUpdateScopingMemo(loanId: string, reviewId: string) {
  const qc = useQueryClient();
  return useMutation<LoanLegalReviewDto, unknown, UpdateScopingMemoRequest>({
    mutationFn: (body) =>
      loanLegalReviewsApi.updateScopingMemo(loanId, reviewId, body),
    onSuccess: () => invalidateReview(qc, loanId, reviewId),
  });
}

export function useRecordModuleConclusion(loanId: string, reviewId: string) {
  const qc = useQueryClient();
  return useMutation<
    LoanLegalReviewDto,
    unknown,
    RecordModuleConclusionRequest
  >({
    mutationFn: (body) =>
      loanLegalReviewsApi.recordModuleConclusion(loanId, reviewId, body),
    onSuccess: () => invalidateReview(qc, loanId, reviewId),
  });
}

export function useGenerateDraftLlcr(loanId: string, reviewId: string) {
  const qc = useQueryClient();
  return useMutation<LoanLegalReviewDto, unknown, void>({
    mutationFn: () => loanLegalReviewsApi.generateDraft(loanId, reviewId),
    onSuccess: () => invalidateReview(qc, loanId, reviewId),
  });
}

export function useSubmitForReview(loanId: string, reviewId: string) {
  const qc = useQueryClient();
  return useMutation<LoanLegalReviewDto, unknown, void>({
    mutationFn: () => loanLegalReviewsApi.submitForReview(loanId, reviewId),
    onSuccess: () => invalidateReview(qc, loanId, reviewId),
  });
}

export function useRecordSupervisorAcceptance(
  loanId: string,
  reviewId: string,
) {
  const qc = useQueryClient();
  return useMutation<
    LoanLegalReviewDto,
    unknown,
    SupervisorAcceptanceRequest
  >({
    mutationFn: (body) =>
      loanLegalReviewsApi.recordSupervisorAcceptance(loanId, reviewId, body),
    onSuccess: () => invalidateReview(qc, loanId, reviewId),
  });
}

export function useFinalizeLoanLegalReview(loanId: string, reviewId: string) {
  const qc = useQueryClient();
  return useMutation<
    LoanLegalReviewDto,
    unknown,
    FinalizeLoanLegalReviewRequest
  >({
    mutationFn: (body) => loanLegalReviewsApi.finalize(loanId, reviewId, body),
    onSuccess: () => invalidateReview(qc, loanId, reviewId),
  });
}

export function useReissueLoanLegalReview(loanId: string, reviewId: string) {
  const qc = useQueryClient();
  return useMutation<
    LoanLegalReviewDto,
    unknown,
    ReissueLoanLegalReviewRequest
  >({
    mutationFn: (body) => loanLegalReviewsApi.reissue(loanId, reviewId, body),
    onSuccess: () => invalidateReview(qc, loanId, reviewId),
  });
}

// ============================== Memos (loan) =============================

export function useLegalReviewMemos(loanId: string | undefined) {
  return useQuery<LegalReviewMemoDto[]>({
    queryKey: legalReviewKeys.memos(loanId ?? ""),
    queryFn: () => legalReviewMemosApi.list(loanId!),
    enabled: !!loanId,
  });
}

export function useCreateLegalReviewMemo(loanId: string) {
  const qc = useQueryClient();
  return useMutation<LegalReviewMemoDto, unknown, CreateLegalReviewMemoRequest>({
    mutationFn: (body) => legalReviewMemosApi.create(loanId, body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: legalReviewKeys.memos(loanId) }),
  });
}

export function useSignLegalReviewMemo(loanId: string, memoId: string) {
  const qc = useQueryClient();
  return useMutation<LegalReviewMemoDto, unknown, SignLegalReviewMemoRequest>({
    mutationFn: (body) => legalReviewMemosApi.sign(loanId, memoId, body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: legalReviewKeys.memos(loanId) }),
  });
}

// ============================== Engagement reads (pool) ==================

export function useSpvPoolLegalReviews(poolId: string | undefined) {
  return useQuery<Array<PoolLegalReviewDto | PoolLegalReviewSummaryDto>>({
    queryKey: legalReviewKeys.reviewsForPool(poolId ?? ""),
    queryFn: () => poolLegalReviewsApi.list(poolId!),
    enabled: !!poolId,
  });
}

export function useSpvPoolLegalReview(
  poolId: string | undefined,
  reviewId: string | undefined,
) {
  return useQuery<PoolLegalReviewDto | PoolLegalReviewSummaryDto>({
    queryKey: legalReviewKeys.poolReview(poolId ?? "", reviewId ?? ""),
    queryFn: () => poolLegalReviewsApi.get(poolId!, reviewId!),
    enabled: !!poolId && !!reviewId,
  });
}

// ============================== Engagement writes (pool) =================

export function useCreateSpvPoolLegalReview(poolId: string) {
  const qc = useQueryClient();
  return useMutation<
    PoolLegalReviewDto,
    unknown,
    CreatePoolLegalReviewRequest
  >({
    mutationFn: (body) => poolLegalReviewsApi.create(poolId, body),
    onSuccess: () => invalidatePoolReview(qc, poolId),
  });
}

export function useUpdatePoolScopingMemo(poolId: string, reviewId: string) {
  const qc = useQueryClient();
  return useMutation<
    PoolLegalReviewDto,
    unknown,
    UpdatePoolScopingMemoRequest
  >({
    mutationFn: (body) =>
      poolLegalReviewsApi.updateScopingMemo(poolId, reviewId, body),
    onSuccess: () => invalidatePoolReview(qc, poolId, reviewId),
  });
}

export function useRecordPoolModuleConclusion(
  poolId: string,
  reviewId: string,
) {
  const qc = useQueryClient();
  return useMutation<
    PoolLegalReviewDto,
    unknown,
    RecordPoolModuleConclusionRequest
  >({
    mutationFn: (body) =>
      poolLegalReviewsApi.recordModuleConclusion(poolId, reviewId, body),
    onSuccess: () => invalidatePoolReview(qc, poolId, reviewId),
  });
}

export function useGenerateDraftPlcr(poolId: string, reviewId: string) {
  const qc = useQueryClient();
  return useMutation<PoolLegalReviewDto, unknown, void>({
    mutationFn: () => poolLegalReviewsApi.generateDraft(poolId, reviewId),
    onSuccess: () => invalidatePoolReview(qc, poolId, reviewId),
  });
}

export function useGenerateOfficerCertSupport(
  poolId: string,
  reviewId: string,
) {
  const qc = useQueryClient();
  return useMutation<PoolLegalReviewDto, unknown, void>({
    mutationFn: () =>
      poolLegalReviewsApi.generateOfficerCertSupport(poolId, reviewId),
    onSuccess: () => invalidatePoolReview(qc, poolId, reviewId),
  });
}

export function useSubmitPoolForReview(poolId: string, reviewId: string) {
  const qc = useQueryClient();
  return useMutation<PoolLegalReviewDto, unknown, void>({
    mutationFn: () => poolLegalReviewsApi.submitForReview(poolId, reviewId),
    onSuccess: () => invalidatePoolReview(qc, poolId, reviewId),
  });
}

export function useRecordPoolSupervisorAcceptance(
  poolId: string,
  reviewId: string,
) {
  const qc = useQueryClient();
  return useMutation<
    PoolLegalReviewDto,
    unknown,
    PoolSupervisorAcceptanceRequest
  >({
    mutationFn: (body) =>
      poolLegalReviewsApi.recordSupervisorAcceptance(poolId, reviewId, body),
    onSuccess: () => invalidatePoolReview(qc, poolId, reviewId),
  });
}

export function useFinalizeSpvPoolLegalReview(
  poolId: string,
  reviewId: string,
) {
  const qc = useQueryClient();
  return useMutation<
    PoolLegalReviewDto,
    unknown,
    FinalizePoolLegalReviewRequest
  >({
    mutationFn: (body) => poolLegalReviewsApi.finalize(poolId, reviewId, body),
    onSuccess: () => invalidatePoolReview(qc, poolId, reviewId),
  });
}

export function useReissueSpvPoolLegalReview(
  poolId: string,
  reviewId: string,
) {
  const qc = useQueryClient();
  return useMutation<
    PoolLegalReviewDto,
    unknown,
    ReissuePoolLegalReviewRequest
  >({
    mutationFn: (body) => poolLegalReviewsApi.reissue(poolId, reviewId, body),
    onSuccess: () => invalidatePoolReview(qc, poolId, reviewId),
  });
}

// ============================== Memos (pool) =============================

export function usePoolLegalReviewMemos(poolId: string | undefined) {
  return useQuery<PoolLegalReviewMemoDto[]>({
    queryKey: legalReviewKeys.poolMemos(poolId ?? ""),
    queryFn: () => poolLegalReviewMemosApi.list(poolId!),
    enabled: !!poolId,
  });
}

export function useCreatePoolLegalReviewMemo(poolId: string) {
  const qc = useQueryClient();
  return useMutation<
    PoolLegalReviewMemoDto,
    unknown,
    CreatePoolLegalReviewMemoRequest
  >({
    mutationFn: (body) => poolLegalReviewMemosApi.create(poolId, body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: legalReviewKeys.poolMemos(poolId) }),
  });
}

export function useSignPoolLegalReviewMemo(poolId: string, memoId: string) {
  const qc = useQueryClient();
  return useMutation<
    PoolLegalReviewMemoDto,
    unknown,
    SignPoolLegalReviewMemoRequest
  >({
    mutationFn: (body) => poolLegalReviewMemosApi.sign(poolId, memoId, body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: legalReviewKeys.poolMemos(poolId) }),
  });
}
